import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { legends } from '../utils.RiskMatrix';
import { cardGap, cardSx } from './cardSx';
import { gradeBarWidths } from './gradeBar';
import { GradeName, GradeThresholds } from './riskSetupDraft.types';
import { gradeLabel } from './WorkedExample';

export interface RiskGradeCardProps {
	thresholds: GradeThresholds;
	/** Top of the grade scale — Grades.Max, which rescales with the thresholds
	 *  when normalizing (unlike the raw max overall score). */
	gradeMax: number;
	/** RiskGrade's existing editable thresholds table (riskGrade-table). */
	thresholdsTable: React.ReactNode;
}

const show = (value: number): string =>
	Number.isNaN(value) ? '' : String(value);

export const RiskGradeCard = ({
	thresholds,
	gradeMax,
	thresholdsTable,
}: RiskGradeCardProps) => {
	const { t } = useGlobalization();
	const bounds: Record<GradeName, [number, number]> = {
		negligible: [0, thresholds.low],
		low: [thresholds.low, thresholds.medium],
		medium: [thresholds.medium, thresholds.high],
		high: [thresholds.high, thresholds.extreme],
		extreme: [thresholds.extreme, gradeMax],
	};
	const widths = gradeBarWidths(thresholds, gradeMax);
	const description = t(
		// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
		"Set the score thresholds for each risk grade. A pipe's overall risk score determines its grade. Adjust the thresholds to fit your risk criteria.",
	);

	return (
		<Paper elevation={0} sx={{ ...cardSx, mt: cardGap }}>
			<Typography
				sx={{
					fontSize: 16,
					fontWeight: 700,
					color: '#0d1520',
					mb: 0.5,
				}}>
				{t('Risk Grade')}
			</Typography>
			<Typography sx={{ fontSize: 13, color: '#5b6472', mb: 1.25 }}>
				{description}
			</Typography>
			<Box
				data-cy="grade-bar"
				sx={{
					display: 'flex',
					height: 14,
					borderRadius: '7px',
					overflow: 'hidden',
					border: '1px solid #eceef2',
				}}>
				{legends.map((legend, i) => (
					<Box
						key={legend.name}
						data-cy={`grade-bar-${legend.name}`}
						style={{ width: `${widths[i]}%` }}
						sx={{ background: legend.color }}
					/>
				))}
			</Box>
			<Box
				sx={{
					display: 'flex',
					flexWrap: 'wrap',
					gap: 1.75,
					mt: 1,
					mb: 2,
				}}>
				{legends.map(legend => {
					const name = legend.name as GradeName;
					const [from, to] = bounds[name];
					return (
						<Box
							key={name}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
							}}>
							<Box
								data-cy={`grade-swatch-${name}`}
								sx={{
									width: 10,
									height: 10,
									borderRadius: '2px',
									background: legend.color,
									flex: 'none',
								}}
							/>
							<Typography
								sx={{
									fontSize: 12,
									fontWeight: 600,
									color: '#3a4451',
								}}>
								{gradeLabel(name, t)}
							</Typography>
							<Typography sx={{ fontSize: 12, color: '#9aa2ad' }}>
								{`${show(from)} – ${show(to)}`}
							</Typography>
						</Box>
					);
				})}
			</Box>
			<Box
				data-cy="grade-thresholds-container"
				sx={{
					border: '1px solid #e3e8ef',
					borderRadius: '6px',
					overflow: 'hidden',
					// Scoped to this container only: drop the table's own Paper
					// shadow so it doesn't read as a card-in-a-card.
					'& .MuiPaper-root': { boxShadow: 'none' },
				}}>
				{thresholdsTable}
			</Box>
		</Paper>
	);
};
